import { groundFor, paletteKey, type Ground } from '@wellread/core';
import { type ButtonHTMLAttributes, type CSSProperties, type ReactNode, type Ref, useState } from 'react';
import { cx } from './cx';
import { Icon, type IconName } from './Icon';

export { cx };
export { Glyph, Icon, type IconName } from './Icon';
export { Logo, Mark } from './Logo';

type Variant = 'primary' | 'secondary' | 'quiet' | 'danger';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'sm';
  /** A keyboard shortcut printed inside the label. */
  kbd?: string;
}

export function Button({ variant = 'secondary', size, kbd, className, children, ...rest }: ButtonProps) {
  return (
    <button type="button" className={cx('btn', `btn-${variant}`, size && 'btn-sm', className)} {...rest}>
      {children}
      {kbd ? <kbd>{kbd}</kbd> : null}
    </button>
  );
}

/**
 * Where a question is from (its book and concept) gives its answer away, so
 * it waits behind Where is this from? unless show is set: the reader's
 * setting, or a question already answered. Revealed, it stays for that
 * question.
 */
export function SourceReveal({ show, children }: { show: boolean; children: ReactNode }) {
  const [reveal, setReveal] = useState(false);
  return show || reveal ? (
    <>{children}</>
  ) : (
    <Button variant="quiet" size="sm" onClick={() => setReveal(true)}>
      Where is this from?
    </Button>
  );
}

export type Tone = 'track' | 'due' | 'good' | 'miss';

export function Chip({
  tone,
  dot,
  dotColor,
  children,
  onClick,
}: {
  tone?: Tone;
  dot?: boolean;
  dotColor?: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  const inner = (
    <>
      {dot || dotColor ? (
        <span className="dot" style={dotColor ? { background: dotColor } : undefined} />
      ) : null}
      {children}
    </>
  );
  return onClick ? (
    <button type="button" className={cx('chip', tone)} onClick={onClick}>
      {inner}
    </button>
  ) : (
    <span className={cx('chip', tone)}>{inner}</span>
  );
}

/**
 * How far something has got. value: a percentage; left out while there is
 * no telling yet, and the bar then runs without one (it stands still under
 * reduced motion).
 */
export function Meter({ value, tone, label }: { value?: number; tone?: 'due' | 'faint'; label?: string }) {
  if (value === undefined) {
    return (
      <div className={cx('meter', 'busy', tone)} role="progressbar" aria-label={label}>
        <i />
      </div>
    );
  }
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      className={cx('meter', tone)}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pct)}
      aria-label={label}
    >
      <i style={{ width: `${pct}%` }} />
    </div>
  );
}

export function Stat({ value, label }: { value: ReactNode; label: string }) {
  return (
    <span className="stat">
      <b>{value}</b>
      <span>{label}</span>
    </span>
  );
}

export interface SegOption<T extends string> {
  value: T;
  label: string;
}

/** A segmented control. The selected segment is aria-pressed. */
export function Seg<T extends string>({
  label,
  options,
  value,
  onChange,
  full,
  className,
}: {
  label: string;
  options: readonly (SegOption<T> | T)[];
  value: T;
  onChange: (v: T) => void;
  full?: boolean;
  className?: string;
}) {
  return (
    <div className={cx('seg', full && 'full', className)} role="group" aria-label={label}>
      {options.map((o) => {
        const opt = typeof o === 'string' ? { value: o, label: o } : o;
        const on = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            className={cx(on && 'on')}
            aria-pressed={on}
            onClick={() => onChange(opt.value)}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

export function Switch({
  on,
  onChange,
  label,
  disabled,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={cx('sw', on && 'on')}
      disabled={disabled}
      onClick={() => onChange(!on)}
    />
  );
}

export function Kv({ label, children }: { label: ReactNode; children: ReactNode }) {
  return (
    <div className="kv">
      <span>{label}</span>
      {typeof children === 'string' || typeof children === 'number' ? <b>{children}</b> : children}
    </div>
  );
}

export const groundVar = (g: string | undefined, title: string) =>
  `var(--ground-${(g as Ground | undefined) ?? groundFor(title)})`;

export interface CoverProps {
  title: string;
  author?: string;
  src?: string;
  ground?: string;
  /** fill: as wide as its box, lettering scaled to match. */
  size?: 'xl' | 'lg' | 'md' | 'sm' | 'fill';
  /** State goes on a cover only while the book is building. */
  busy?: { label?: string; progress?: number };
}

/** 2:3, always. The book's own image when it has one, otherwise the generated cover. */
export function Cover({ title, author, src, ground, size = 'lg', busy }: CoverProps) {
  const style = { '--cg': groundVar(ground, title) } as CSSProperties;
  return (
    <span
      className={cx('cov', `cov-${size}`, busy && 'busy')}
      style={style}
      role="img"
      aria-label={author ? `${title}, ${author}` : title}
    >
      {src ? (
        <img src={src} alt="" loading="lazy" decoding="async" />
      ) : (
        <span className="cov-in">
          <span className={cx('cov-t', longestWord(title) > 11 && 'long')}>{title}</span>
          <span className="cov-r" />
          <span className="cov-a">{author}</span>
        </span>
      )}
      {busy ? (
        <span className="veil">
          <span className="lab">{busy.label ?? 'Extracting'}</span>
          <span className="bar">
            <i style={{ width: `${busy.progress ?? 0}%` }} />
          </span>
        </span>
      ) : null}
    </span>
  );
}

/**
 * The page header: breadcrumb, title, one line, actions. cover: a book's
 * cover as a small thumbnail beside the title; className says where it
 * shows and whether the header is drawn as one short row (app.css, .top).
 */
export function Top({
  ref,
  className,
  crumb,
  cover,
  title,
  sub,
  children,
}: {
  ref?: Ref<HTMLDivElement>;
  className?: string;
  crumb?: ReactNode;
  cover?: ReactNode;
  title: ReactNode;
  sub?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div ref={ref} className={cx('top', className)}>
      {cover ? <div className="top-cover">{cover}</div> : null}
      <div>
        {crumb ? (
          <nav className="crumb" aria-label="Breadcrumb">
            {crumb}
          </nav>
        ) : null}
        <h1>{title}</h1>
        {sub ? <p className="sub">{sub}</p> : null}
      </div>
      {children ? <div className="acts">{children}</div> : null}
    </div>
  );
}

/** One reasoning step: got, missed, contradicted, or not graded yet. */
export function Step({
  n,
  result,
  children,
}: {
  n: number;
  result?: 'got' | 'missed' | 'contradicted';
  children: ReactNode;
}) {
  const mark =
    result === 'got' ? ' ✓' : result === 'contradicted' ? ' ✕ against' : result === 'missed' ? ' ✕' : '';
  return (
    <div className="qstep">
      <span
        className={cx(
          'n',
          result === 'got' && 'y',
          (result === 'missed' || result === 'contradicted') && 'x',
        )}
      >
        {`STEP ${n}${mark}`}
      </span>
      <span className={cx('t', result === 'got' && 'y')}>{children}</span>
    </div>
  );
}

/**
 * The bar across the top of every signed-in page that isn't full-screen:
 * the logo, the app-wide places and Review on the left; search, Feedback
 * and the account on the right. On a phone the places move to a tab bar
 * along the bottom, and Review comes over beside search.
 */
export function AppBar({
  brand,
  nav,
  review,
  onSearch,
  feedback,
  account,
}: {
  brand: ReactNode;
  /** Links, the current one with className "on"; Review last. */
  nav: ReactNode;
  /** Review as a phone's bar has it, beside search (className "barreview"); shown only there. */
  review?: ReactNode;
  onSearch: () => void;
  /** The way to say what you think of the page you're on, between search and the account. */
  feedback?: ReactNode;
  account: ReactNode;
}) {
  return (
    <header className="appbar">
      <div className="barstart">
        {brand}
        <nav className="barnav" aria-label="Main">
          {nav}
        </nav>
      </div>
      <div className="barend">
        {review}
        <button type="button" className="barsearch" onClick={onSearch} aria-label="Search your library">
          <Icon name="search" size={16} />
          <span className="lbl">Search your library</span>
          <kbd>{paletteKey}</kbd>
        </button>
        {feedback}
        {account}
      </div>
    </header>
  );
}

/**
 * The phone's tab bar, as the iPhone app draws it: the top bar's places, each
 * an icon and a label, on a floating capsule, the current one on a pill.
 */
export function TabBar({ children }: { children: ReactNode }) {
  return (
    <nav className="tabbar" aria-label="Main">
      {children}
    </nav>
  );
}

/** A place's icon in the tab bar. The hero's is an accent disc, whichever place is current. */
export function TabIcon({ name, hero = false }: { name: IconName; hero?: boolean }) {
  if (hero) {
    return (
      <span className="tab-hero">
        <Icon name={name} size={16} />
      </span>
    );
  }
  return <Icon name={name} size={22} />;
}

export type Theme = 'system' | 'light' | 'dark';

/** The themes, worded the same wherever they are chosen. */
export const THEMES: readonly SegOption<Theme>[] = [
  { value: 'system', label: 'Match device' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/**
 * The account menu that opens from the avatar: who is signed in, light or
 * dark in one tap, and the way to Settings and out. The colour is chosen in
 * Settings. Presentational, so the gallery can render it without a session;
 * the shell owns opening and closing.
 */
export function AccountMenu({
  name,
  email,
  theme,
  onTheme,
  children,
}: {
  name: string;
  email: string;
  theme: Theme;
  onTheme: (t: Theme) => void;
  /** The rows at the foot, each an icon and a label: Settings, Feedback (on a narrow window), Email us, Sign out. */
  children: ReactNode;
}) {
  return (
    <div className="acctmenu" role="menu" aria-label="Account">
      <div className="acct-id">
        <b>{name}</b>
        <span>{email}</span>
      </div>
      <div className="grp">
        <span className="lab">Theme</span>
        <Seg label="Theme" full value={theme} onChange={onTheme} options={THEMES} />
      </div>
      <div className="acts">{children}</div>
    </div>
  );
}

export function Initials({ name, size }: { name: string; size?: 'lg' }) {
  return <span className={cx('av', size)}>{initials(name)}</span>;
}

/** The longest run of non-space characters: a long word has to be set smaller. */
function longestWord(s: string): number {
  return s.split(/\s+/).reduce((n, w) => Math.max(n, w.length), 0);
}

export function initials(name: string): string {
  const words = name
    .replace(/[^\p{L}\p{N}\s'-]/gu, ' ')
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '?';
  if (words.length === 1) return words[0]!.slice(0, 2).toUpperCase();
  return (words[0]![0]! + words[words.length - 1]![0]!).toUpperCase();
}
